import { Form } from 'react-final-form'
import { Box, Grid2, IconButton, Stack, Typography } from '@mui/material'
import { TextField } from 'src/shared/form/control/TextField'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { composeValidators, maxChar, required } from 'src/shared/form/validation/validators'
import Alert from '@mui/material/Alert'
import { DirtyFormSpy } from 'src/shared/form/dirty/DirtyFormSpy'
import { createDecorators } from 'src/shared/form/utils/decorators'
import { TEXT_LENGTH } from 'src/shared/constants/constants'
import { AddButton } from 'src/shared/button/Buttons'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import React, { useCallback } from 'react'
import { HELP_AND_INSTRUCTIONS_KEY_SEARCH_PARAM } from 'src/screens/administration/configuration-management/help-instructions-configuration/HelpInstructionsGridUtils'
import { ROUTES } from 'src/routing/routes'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { FieldArray } from 'react-final-form-arrays'
import arrayMutators from 'final-form-arrays'
import ClearIcon from '@mui/icons-material/Clear'

export interface HelpInstructionsConfigFormValues {
  deLabel: string
  deLink: string
  enLabel: string
  enLink: string
  frLabel: string
  frLink: string
  itLabel: string
  itLink: string
  key: string
  locationHints: string[]
}

interface HelpInstructionsConfigFormProps {
  mode: 'CREATE' | 'EDIT'
  onSubmit: (values: HelpInstructionsConfigFormValues) => Promise<void>
  initialValues: HelpInstructionsConfigFormValues
  formDirty?: boolean
  disableKeyField?: boolean
}

const MAX_NUMBER_OF_LOCATION_HINTS = 10

export const HelpInstructionsConfigForm = ({
  mode,
  onSubmit,
  initialValues,
  formDirty,
  disableKeyField,
}: HelpInstructionsConfigFormProps) => {
  const { getMessage } = useMessageSource()
  const decorators = createDecorators()
  const navigate = useDelayedNavigate()

  const handleAddHelpInstructionsLinkAtSameLocation = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, helpAndInstructionsKey: string) => {
      e.stopPropagation()

      const searchParamsString = `${HELP_AND_INSTRUCTIONS_KEY_SEARCH_PARAM}=${helpAndInstructionsKey}`
      navigate(`${ROUTES.HelpInstructionsConfigurationCreate.params({})}?${searchParamsString}`)
    },
    [navigate],
  )

  return (
    <Stack spacing={3}>
      <Alert severity="warning">{getMessage('label.warning.config.edit')}</Alert>
      <Form<HelpInstructionsConfigFormValues>
        initialValues={initialValues}
        onSubmit={onSubmit}
        decorators={decorators}
        mutators={{ ...arrayMutators }}
        render={({ handleSubmit }) => {
          return (
            <form id="help-instructions-config-form" onSubmit={handleSubmit} noValidate>
              <Stack spacing={4}>
                <Grid2 container columnSpacing={5}>
                  <Grid2 size={6}>
                    <TextField
                      name="deLabel"
                      label={`${getMessage('label.configuration.help.instructions.grid.label.locale')} DE`}
                      required
                      validate={required()}
                    />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField name="deLink" label={`${getMessage('label.link')} DE`} required validate={required()} />
                  </Grid2>

                  <Grid2 size={6}>
                    <TextField
                      name="enLabel"
                      label={`${getMessage('label.configuration.help.instructions.grid.label.locale')} EN`}
                      required
                      validate={required()}
                    />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField name="enLink" label={`${getMessage('label.link')} EN`} required validate={required()} />
                  </Grid2>

                  <Grid2 size={6}>
                    <TextField
                      name="frLabel"
                      label={`${getMessage('label.configuration.help.instructions.grid.label.locale')} FR`}
                      required
                      validate={required()}
                    />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField name="frLink" label={`${getMessage('label.link')} FR`} required validate={required()} />
                  </Grid2>

                  <Grid2 size={6}>
                    <TextField
                      name="itLabel"
                      label={`${getMessage('label.configuration.help.instructions.grid.label.locale')} IT`}
                      required
                      validate={required()}
                    />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField name="itLink" label={`${getMessage('label.link')} IT`} required validate={required()} />
                  </Grid2>
                </Grid2>

                <Stack spacing={0}>
                  <Typography variant="h2">
                    {getMessage('label.configuration.help.and.instructions.edit.location')}
                  </Typography>
                  {!disableKeyField && (
                    <Alert severity="info" sx={{ marginTop: 2 }}>
                      {getMessage('label.configuration.help.and.instructions.edit.key.info')}
                    </Alert>
                  )}
                  <TextField
                    name="key"
                    label={getMessage('label.configuration.help.and.instructions.edit.key')}
                    required
                    validate={composeValidators(required(), maxChar(TEXT_LENGTH.M))}
                    disabled={disableKeyField}
                    sx={{ marginTop: 2 }}
                  />
                  {mode === 'EDIT' && (
                    <Box display="flex" justifyContent="start">
                      <AddButton
                        messageKey={'label.configuration.help.instructions.add.at.same.location.button'}
                        startIcon={<AddOutlinedIcon />}
                        onClick={(e) => handleAddHelpInstructionsLinkAtSameLocation(e, initialValues.key)}
                      />
                    </Box>
                  )}
                </Stack>

                <Stack spacing={2}>
                  <Typography variant="h2">
                    {getMessage('label.configuration.help.instructions.location.hints')}
                  </Typography>
                  <FieldArray name="locationHints">
                    {({ fields }) => {
                      const numberOfHintFields = fields.length ?? 0

                      return (
                        <Stack spacing={1}>
                          {fields.map((name, index) => (
                            <Stack key={name} direction="row" spacing={2} alignItems="center">
                              <TextField
                                name={name}
                                label={`${getMessage('label.configuration.help.instructions.location.hints')} ${
                                  index + 1
                                }`}
                                validate={composeValidators(required(), maxChar(TEXT_LENGTH.L))}
                                sx={{ flex: 1 }}
                              />
                              <IconButton
                                aria-label="remove"
                                onClick={() => fields.remove(index)}
                                size="small"
                                sx={{ top: -7 }}
                              >
                                <ClearIcon />
                              </IconButton>
                            </Stack>
                          ))}

                          <Box display="flex" justifyContent="start">
                            <AddButton
                              messageKey={'label.configuration.help.instructions.add.location.hint'}
                              startIcon={<AddOutlinedIcon />}
                              onClick={() => fields.push('')}
                              disabled={numberOfHintFields >= MAX_NUMBER_OF_LOCATION_HINTS}
                            />
                          </Box>
                        </Stack>
                      )
                    }}
                  </FieldArray>
                </Stack>
                <DirtyFormSpy gridDirty={formDirty} />
              </Stack>
            </form>
          )
        }}
      />
    </Stack>
  )
}
